/**
 * Stage 7 explanation generation (docs/03 §8).
 * Templates read only stored ScoreTrace slots. Missing required values throw.
 */

import type { ScoreTrace } from './types';

export type ExplanationToken =
  | 'WON_ON_RATE'
  | 'WON_ON_OFFER'
  | 'WON_ON_TIER'
  | 'GRACE_OK'
  | 'CREDIT_OK'
  | 'UTIL_EFFECT'
  | 'FX_OK'
  | 'RUNNER_UP';

/** Fixed assembly order from docs/03 §8. */
export const EXPLANATION_TOKEN_ORDER: readonly ExplanationToken[] = [
  'WON_ON_RATE',
  'WON_ON_OFFER',
  'WON_ON_TIER',
  'GRACE_OK',
  'CREDIT_OK',
  'UTIL_EFFECT',
  'FX_OK',
  'RUNNER_UP',
] as const;

export class MissingExplanationValueError extends Error {
  readonly token: ExplanationToken;
  readonly field: string;

  constructor(token: ExplanationToken, field: string) {
    super(`Explanation template ${token} missing required trace field: ${field}`);
    this.name = 'MissingExplanationValueError';
    this.token = token;
    this.field = field;
  }
}

function requireField<T>(token: ExplanationToken, field: string, value: T | null | undefined): T {
  if (value === null || value === undefined) {
    throw new MissingExplanationValueError(token, field);
  }
  return value;
}

/** Format integer bps as a percent string with no float literals (150 → "1.5%"). */
export function formatBpsAsPercent(bps: number): string {
  if (!Number.isInteger(bps)) throw new Error('bps must be an integer');
  const sign = bps < 0 ? '-' : '';
  const abs = Math.abs(bps);
  const whole = Math.floor(abs / 100);
  const frac = abs % 100;
  if (frac === 0) return `${sign}${whole}%`;
  const fracStr = String(frac).padStart(2, '0').replace(/0+$/, '');
  return `${sign}${whole}.${fracStr}%`;
}

/** Format cents as $D.CC with no float arithmetic. */
export function formatCentsDisplay(cents: bigint): string {
  const neg = cents < 0n;
  const abs = neg ? -cents : cents;
  const dollars = abs / 100n;
  const rem = abs % 100n;
  const core = `${dollars}.${rem.toString().padStart(2, '0')}`;
  return neg ? `-$${core}` : `$${core}`;
}

function renderWonOnRate(trace: ScoreTrace): string {
  const rate = requireField('WON_ON_RATE', 'slot_rate', trace.slot_rate);
  return `Highest verified reward among eligible cards: ${rate} on ${trace.category_resolved}.`;
}

function renderWonOnOffer(trace: ScoreTrace): string {
  const value = requireField('WON_ON_OFFER', 'slot_offer_value', trace.slot_offer_value);
  const rate = requireField('WON_ON_OFFER', 'slot_base_rate', trace.slot_base_rate);
  return `Activated offer adds ${value} on top of the base ${rate}.`;
}

function renderWonOnTier(trace: ScoreTrace): string {
  const benefit = requireField('WON_ON_TIER', 'slot_benefit', trace.slot_benefit);
  const mode = requireField('WON_ON_TIER', 'slot_mode', trace.slot_mode);
  return `Only card with confirmed ${benefit} under ${mode}.`;
}

function renderGraceOk(trace: ScoreTrace): string {
  const date = requireField('GRACE_OK', 'slot_grace_due_date', trace.slot_grace_due_date);
  return `Grace period derived active — last statement paid in full by its due date on ${date}.`;
}

function renderCreditOk(trace: ScoreTrace): string {
  const avail = requireField('CREDIT_OK', 'slot_available_credit', trace.slot_available_credit);
  const amount = requireField('CREDIT_OK', 'slot_purchase_amount', trace.slot_purchase_amount);
  return `Available credit ${avail} covers the ${amount} purchase.`;
}

function renderUtilEffect(trace: ScoreTrace): string {
  const before = requireField('UTIL_EFFECT', 'slot_util_before', trace.slot_util_before);
  const after = requireField('UTIL_EFFECT', 'slot_util_after', trace.slot_util_after);
  const date = requireField(
    'UTIL_EFFECT',
    'slot_statement_close_date',
    trace.slot_statement_close_date,
  );
  return (
    `Card utilization after purchase: ${before} → ${after}. If a payment POSTS ` +
    `before the ${date} statement close the reported figure should be lower — but ` +
    `posting is not scheduling, issuers hold payments, and some report off-cycle. ` +
    `Treated as unmitigated unless you confirm.`
  );
}

function renderFxOk(trace: ScoreTrace): string {
  const date = requireField('FX_OK', 'slot_fx_verified_date', trace.slot_fx_verified_date);
  return `No foreign transaction fee on this card (verified ${date}).`;
}

function renderRunnerUp(trace: ScoreTrace): string {
  const card = requireField('RUNNER_UP', 'slot_runner_up_card', trace.slot_runner_up_card);
  const delta = requireField('RUNNER_UP', 'slot_runner_up_delta', trace.slot_runner_up_delta);
  const diff = requireField(
    'RUNNER_UP',
    'slot_runner_up_differentiator',
    trace.slot_runner_up_differentiator,
  );
  return `Second: ${card}. ${delta} less in expected reward. ${diff}`;
}

const RENDERERS: Record<ExplanationToken, (trace: ScoreTrace) => string> = {
  WON_ON_RATE: renderWonOnRate,
  WON_ON_OFFER: renderWonOnOffer,
  WON_ON_TIER: renderWonOnTier,
  GRACE_OK: renderGraceOk,
  CREDIT_OK: renderCreditOk,
  UTIL_EFFECT: renderUtilEffect,
  FX_OK: renderFxOk,
  RUNNER_UP: renderRunnerUp,
};

/**
 * Assemble explanation sentences from trace tokens in fixed order.
 * Throws MissingExplanationValueError when a fired token lacks a required slot.
 */
export function generateExplanation(trace: ScoreTrace): string[] {
  const present = new Set(trace.tokens);
  const out: string[] = [];
  for (const token of EXPLANATION_TOKEN_ORDER) {
    if (!present.has(token)) continue;
    out.push(RENDERERS[token](trace));
  }
  return out;
}

/** Empty explanation slots — used when building traces before token emission. */
export function emptyExplanationSlots(): Pick<
  ScoreTrace,
  | 'slot_rate'
  | 'slot_offer_value'
  | 'slot_base_rate'
  | 'slot_benefit'
  | 'slot_mode'
  | 'slot_grace_due_date'
  | 'slot_available_credit'
  | 'slot_purchase_amount'
  | 'slot_util_before'
  | 'slot_util_after'
  | 'slot_statement_close_date'
  | 'slot_fx_verified_date'
  | 'slot_runner_up_card'
  | 'slot_runner_up_delta'
  | 'slot_runner_up_differentiator'
> {
  return {
    slot_rate: null,
    slot_offer_value: null,
    slot_base_rate: null,
    slot_benefit: null,
    slot_mode: null,
    slot_grace_due_date: null,
    slot_available_credit: null,
    slot_purchase_amount: null,
    slot_util_before: null,
    slot_util_after: null,
    slot_statement_close_date: null,
    slot_fx_verified_date: null,
    slot_runner_up_card: null,
    slot_runner_up_delta: null,
    slot_runner_up_differentiator: null,
  };
}
